<template>
  <div class="row">
    <div class="col-md-12">
      <card>
        <div slot="header">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <h4 class="card-title">Sammelbuchung Details</h4>
              <p class="card-category">Sammelbuchung Nr. {{ sammelbuchungId }}</p>
            </div>
            <div>
              <router-link
                :to="`/collective-bookings/${sammelbuchungId}/edit`"
                class="btn btn-warning btn-sm mr-2"
                :class="{ 'disabled': !canEdit }"
                :title="canEdit ? 'Bearbeiten' : 'Bearbeitung nicht mehr möglich'">
                <i class="fa fa-edit"></i> Bearbeiten
              </router-link>
              <a
                :href="downloadUrl"
                target="_blank"
                class="btn btn-info btn-sm">
                <i class="fa fa-print"></i> PDF Drucken
              </a>
            </div>
          </div>
        </div>

        <div v-if="loading" class="text-center">
          <i class="fa fa-spinner fa-spin fa-2x"></i>
          <p>Lade Sammelbuchung...</p>
        </div>

        <div v-else-if="stockRotations.length > 0">
          <!-- General Information -->
          <div class="row mb-4">
            <div class="col-md-6">
              <h6><strong>Erstellt am:</strong></h6>
              <p>{{ formatDate(stockRotations[0].created_at) }}</p>
            </div>
            <div class="col-md-6">
              <h6><strong>Erstellt von:</strong></h6>
              <p>{{ stockRotations[0].user ? stockRotations[0].user.name : '-' }}</p>
            </div>
          </div>

          <!-- Free Text -->
          <div v-if="stockRotations[0].sammelbuchung_free_text" class="mb-4">
            <h6><strong>Freitext:</strong></h6>
            <div class="border p-3 bg-light" v-html="stockRotations[0].sammelbuchung_free_text"></div>
          </div>

          <!-- Notice -->
          <div v-if="stockRotations[0].notice" class="mb-4">
            <h6><strong>Notiz:</strong></h6>
            <p class="border p-3 bg-light">{{ stockRotations[0].notice }}</p>
          </div>

          <!-- Stock Rotations Table -->
          <h5>Lagerbewegungen</h5>
          <div class="table-responsive">
            <table class="table table-striped table-bordered">
              <thead>
                <tr>
                  <th>Produkt</th>
                  <th>Kategorie</th>
                  <th>Menge</th>
                  <th>Grund</th>
                  <th>Preis</th>
                  <th>Gesamtpreis</th>
                  <th v-if="warehouses.length > 1">Lager</th>
                  <th>Kostenstelle</th>
                  <th>Batch</th>
                  <th>Seriennummer</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="rotation in stockRotations" :key="rotation.id">
                  <td>
                    <router-link :to="`/products/${rotation.product_id}`">
                      {{ rotation.product.name }}
                    </router-link>
                    <br>
                    <small class="text-muted">{{ rotation.product.gtin || 'Kein GTIN' }}</small>
                  </td>
                  <td>
                    <span v-if="rotation.product.product_category">
                      {{ rotation.product.product_category.name }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td class="text-right">
                    <span :class="rotation.direction === 'in' ? 'text-success' : 'text-danger'">
                      {{ rotation.direction === 'in' ? '+' : '-' }}{{ rotation.quantity }}
                    </span>
                  </td>
                  <td>
                    <span v-if="rotation.cause">
                      {{ rotation.cause.name }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td class="text-right">
                    <span v-if="rotation.price">
                      {{ formatPrice(rotation.price) }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td class="text-right">
                    <span v-if="rotation.price">
                      {{ formatPrice(rotation.price * rotation.quantity) }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td v-if="warehouses.length > 1">
                    <span v-if="rotation.warehouse">
                      {{ rotation.warehouse.short_name }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <span v-if="rotation.cost_center">
                      {{ rotation.cost_center.name }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <span v-if="rotation.batch">{{ rotation.batch }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <span v-if="rotation.serial_number">{{ rotation.serial_number }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="hasPricing">
                <tr class="table-info">
                  <td colspan="5"><strong>Gesamtsumme:</strong></td>
                  <td class="text-right"><strong>{{ formatPrice(totalAmount) }}</strong></td>
                  <td v-if="warehouses.length > 1"></td>
                  <td colspan="3"></td>
                </tr>
              </tfoot>
            </table>
          </div>

        </div>

        <div v-else class="alert alert-info">
          Keine Lagerbewegungen gefunden.
        </div>

        <div class="text-right mt-3">
          <router-link to="/collective-bookings" class="btn btn-secondary">
            <i class="fa fa-arrow-left"></i> Zurück zur Übersicht
          </router-link>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import api from 'src/api'

export default {
  data() {
    return {
      loading: true,
      sammelbuchungId: null,
      stockRotations: [],
      warehouses: [],
      editTimeLimit: 24,
      print: {
        with_einkaufspreis: false,
        with_verkaufspreis: false,
        showCause: false,
        showBatchAndSerial: false
      }
    }
  },
  computed: {
    canEdit() {
      if (!this.stockRotations.length) return false

      const createdAt = moment(this.stockRotations[0].created_at)
      const now = moment()
      const hoursDiff = now.diff(createdAt, 'hours')

      return hoursDiff <= this.editTimeLimit
    },

    hasPricing() {
      return this.stockRotations.some(rotation => rotation.price && rotation.price > 0)
    },

    totalAmount() {
      return this.stockRotations.reduce((total, rotation) => {
        if (rotation.price && rotation.quantity) {
          return total + (rotation.price * rotation.quantity)
        }
        return total
      }, 0)
    },


    downloadUrl() {
      return `${api}/stock_rotations/grouped_booking/download?sammelbuchung_id=${this.sammelbuchungId}&with_einkaufspreis=${this.print.with_einkaufspreis}&with_verkaufspreis=${this.print.with_verkaufspreis}&show_cause=${this.print.showCause}&show_batch_and_serial=${this.print.showBatchAndSerial}`
    }
  },
  methods: {
    async loadData() {
      this.loading = true

      try {
        // Load collective booking data
        const { data: rotations } = await this.$http.get(`/group/stock_rotations/${this.sammelbuchungId}`)
        this.stockRotations = rotations

        // Load warehouses and settings
        const [warehousesRes, settingsRes] = await Promise.all([
          this.$http.get('/warehouses'),
          this.$http.get('/settings')
        ])

        this.warehouses = warehousesRes.data
        this.editTimeLimit = settingsRes.data.collective_booking_edit_hours || 24

      } catch (error) {
        this.$notify({
          message: 'Fehler beim Laden der Sammelbuchung',
          type: 'danger'
        })
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    formatDate(date) {
      return moment(date).format('DD.MM.YYYY HH:mm')
    },

    formatPrice(price) {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR'
      }).format(price)
    }
  },

  created() {
    this.sammelbuchungId = this.$route.params.id
    this.loadData()
  }
}
</script>

<style scoped>
.card {
  margin-bottom: 1rem;
}

.table th {
  background-color: #f8f9fa;
  border-top: none;
}

.text-success {
  color: #28a745 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

/* Fix multiselect overflow issues */
.table-responsive {
  overflow: visible !important;
}

.table {
  overflow: visible !important;
}

/* Ensure multiselect dropdowns are visible */
.multiselect__content-wrapper {
  z-index: 9999 !important;
}

.multiselect__content {
  z-index: 9999 !important;
}

/* Fix table border radius to match card */
.table {
  border-radius: 0.375rem !important;
  overflow: hidden !important;
}

.table thead th:first-child {
  border-top-left-radius: 0.375rem !important;
}

.table thead th:last-child {
  border-top-right-radius: 0.375rem !important;
}
</style>
